import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Fade,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Gavel,
  FileUpload,
  Add,
  Download,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  useBlacklistBulkUploadMutation,
  useCreateBlacklistEntryMutation,
} from '../services/bankApi';
import { BlacklistEntry } from '../types/bankdetails';
import { AuthMode, TransactionType } from '../services/paymentConfigApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Fade in={value === index}>
      <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
      </Box>
    </Fade>
  );
}

interface BlacklistManagerProps {
  open: boolean;
  onClose: () => void;
  gateway: string;
  initialData?: BlacklistEntry | null;
  onSuccess?: () => void;
}

export default function BlacklistManager({ open, onClose, gateway, initialData, onSuccess }: BlacklistManagerProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(initialData ? 1 : 0);
  const [reason, setreason] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    processed?: number;
    inserted?: number;
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Single entry form state
  const [merchantId, setMerchantId] = useState(initialData?.merchantId || '');
  const [bankName, setBankName] = useState(initialData?.bankName || '');
  const [ifscCode, setIfscCode] = useState(initialData?.ifscCode || '');
  const [paymode, setPaymode] = useState(initialData?.paymode || '');
  const [trtype, setTrtype] = useState(initialData?.trtype || '');
  const [entryreason, setEntryreason] = useState(initialData?.reason || '');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [entryResult, setEntryResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Sync state with initialData when it changes
  useEffect(() => {
    if (initialData) {
      setMerchantId(initialData.merchantId || '');
      setBankName(initialData.bankName || '');
      setIfscCode(initialData.ifscCode || '');
      setPaymode(initialData.paymode || '');
      setTrtype(initialData.trtype || '');
      setEntryreason(initialData.reason || '');
      setIsActive(initialData.isActive ?? true);
      setActiveTab(1);
    }
  }, [initialData]);

  const [bulkUpload, { isLoading: isBulkUploading }] = useBlacklistBulkUploadMutation();
  const [createEntry, { isLoading: isCreatingEntry }] = useCreateBlacklistEntryMutation();

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
    }
  }, []);

  const handleBulkUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadResult({ success: false, message: 'Please select a file to upload' });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('reason', reason);

    try {
      const result = await bulkUpload(formData).unwrap();
      setUploadResult({
        success: true,
        message: result.message,
        processed: result.data?.processed,
        inserted: result.data?.inserted,
        errors: result.data?.errors,
      });
      setSelectedFile(null);
      setreason('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onSuccess?.();
    } catch (error: any) {
      setUploadResult({
        success: false,
        message: error.data?.message || 'Upload failed. Please try again.',
      });
    }
  }, [selectedFile, reason, bulkUpload]);

  const handleCreateEntry = useCallback(async () => {
    if (!merchantId || !bankName || !ifscCode) {
      setEntryResult({ success: false, message: 'Please fill in all required fields' });
      return;
    }

    try {
      const result = await createEntry({
        id: initialData?.id,
        merchantId,
        bankName,
        ifsc: ifscCode,
        gateway,
        isActive,
        reason: entryreason,
        paymode: paymode || undefined,
        trtype: trtype || undefined,
      }).unwrap();
      setEntryResult({ success: true, message: result.message });
      if (!initialData) {
        // Clear form only on create
        setMerchantId('');
        setBankName('');
        setIfscCode('');
        setEntryreason('');
        setPaymode('');
        setTrtype('');
        setIsActive(true);
      }
      onSuccess?.();
    } catch (error: any) {
      setEntryResult({
        success: false,
        message: error.data?.message || 'Failed to process entry. Please try again.',
      });
    }
  }, [merchantId, bankName, ifscCode, entryreason, paymode, trtype, gateway, createEntry, initialData, isActive]);

  const handleClose = useCallback(() => {
    setActiveTab(0);
    setreason('');
    setSelectedFile(null);
    setUploadResult(null);
    if (!initialData) {
      setMerchantId('');
      setBankName('');
      setIfscCode('');
      setEntryreason('');
      setPaymode('');
      setTrtype('');
      setIsActive(true);
    }
    setEntryResult(null);
    onClose();
  }, [onClose, initialData]);

  const handleDownloadSample = useCallback(() => {
    // Sample CSV content with headers and example row
    const csvContent = [
      'merchantId,bankName,ifscCode,gateway,reason',
      'MERCHANT001,State Bank of India,SBIN0001234,Billdesk2,Test blacklist entry',
      'MERCHANT002,HDFC Bank,HDFC0005678,Billdesk2,Another test entry',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'blacklist_sample_format.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Gavel sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Merchant Blacklist Management
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
          }}
        >
          <Tab
            icon={<FileUpload fontSize="small" />}
            iconPosition="start"
            label="Bulk Upload"
          />
          <Tab icon={<Add fontSize="small" />} iconPosition="start" label="Single Entry" />
        </Tabs>

        {/* Bulk Upload Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadSample}
              sx={{ alignSelf: 'flex-start', borderRadius: 2 }}
            >
              Download Sample Format
            </Button>

            <Box
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: selectedFile ? 'primary.main' : alpha(theme.palette.grey[400], 0.5),
                borderRadius: 2,
                bgcolor: selectedFile
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.grey[50], 0.5),
                textAlign: 'center',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500 }}>
                {selectedFile ? selectedFile.name : 'Click to upload Excel file'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedFile
                  ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                  : 'Supports .xlsx, .xls, .csv files'}
              </Typography>
            </Box>

            <TextField
              label="reason (Optional)"
              value={reason}
              onChange={(e) => setreason(e.target.value)}
              placeholder="Enter any reason about this upload"
              multiline
              rows={2}
              fullWidth
            />

            {uploadResult && (
              <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {uploadResult.message}
                </Typography>
                {uploadResult.success && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      size="small"
                      label={`${uploadResult.processed} processed`}
                      color="info"
                    />
                    <Chip
                      size="small"
                      label={`${uploadResult.inserted} inserted`}
                      color="success"
                    />
                  </Box>
                )}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="error">
                      Errors: {uploadResult.errors.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Single Entry Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Merchant ID *"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              placeholder="Enter merchant ID"
              fullWidth
              required
            />

            <TextField
              label="Bank Name *"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Enter bank name"
              fullWidth
              required
            />

            <TextField
              label="IFSC Code *"
              value={ifscCode}
              onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
              placeholder="Enter IFSC code"
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Paymode (Optional)</InputLabel>
                <Select
                  value={paymode}
                  label="Paymode (Optional)"
                  onChange={(e) => setPaymode(e.target.value)}
                >
                  <MenuItem value=""><em>None (All)</em></MenuItem>
                  <MenuItem value={AuthMode.UPI}>UPI</MenuItem>
                  <MenuItem value={AuthMode.NET_BANKING}>Net Banking</MenuItem>
                  <MenuItem value={AuthMode.DEBIT_CARD}>Debit Card</MenuItem>
                  <MenuItem value={AuthMode.AADHAR}>Aadhar</MenuItem>
                  <MenuItem value={AuthMode.PAN}>PAN</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Tr Type (Optional)</InputLabel>
                <Select
                  value={trtype}
                  label="Tr Type (Optional)"
                  onChange={(e) => setTrtype(e.target.value)}
                >
                  <MenuItem value=""><em>None (All)</em></MenuItem>
                  <MenuItem value={TransactionType.LUMPSUM}>Lumpsum</MenuItem>
                  <MenuItem value={TransactionType.EMANDATE}>Emandate</MenuItem>
                  <MenuItem value={TransactionType.HYBRID}>Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="reason (Optional)"
              value={entryreason}
              onChange={(e) => setEntryreason(e.target.value)}
              placeholder="Enter any reason"
              multiline
              rows={2}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Active (Blacklist entry {isActive ? 'enabled' : 'disabled'})
                </Typography>
              }
            />

            {entryResult && (
              <Alert severity={entryResult.success ? 'success' : 'error'} sx={{ borderRadius: 2 }}>
                {entryResult.message}
              </Alert>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {activeTab === 0 ? (
          <Button
            onClick={handleBulkUpload}
            variant="contained"
            disabled={!selectedFile || isBulkUploading}
            startIcon={isBulkUploading ? <CircularProgress size={20} /> : <FileUpload />}
            sx={{ borderRadius: 2 }}
          >
            {isBulkUploading ? 'Uploading...' : 'Upload'}
          </Button>
        ) : (
          <Button
            onClick={handleCreateEntry}
            variant="contained"
            disabled={!merchantId || !bankName || !ifscCode || isCreatingEntry}
            startIcon={isCreatingEntry ? <CircularProgress size={20} /> : <Gavel />}
            sx={{ borderRadius: 2 }}
          >
            {isCreatingEntry ? (initialData ? 'Updating...' : 'Creating...') : (initialData ? 'Update Blacklist' : 'Blacklist')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
